import React, { useState } from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { LayoutDashboard, Building2, Users, FileText, TrendingUp, Briefcase, AlertCircle, X, Settings, ChevronLeft, ChevronRight, Wrench, CircleCheck, ClipboardCheck, SquareKanban, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { TopBar } from '@/components/TopBar';
import { usePermissions } from '@/context/PermissionsContext';
import { usePendingReviewCount } from '@/hooks/usePendingReviewCount';
import { ProfileNameNotice } from '@/components/ProfileNameNotice';
import { RecordDetailsHost } from '@/components/RecordDetails';
import { CommentDialogHost } from '@/components/CommentDialog';
import { PresenceHeartbeat } from '@/components/PresenceHeartbeat';
import { ChatRealtimeHost } from '@/components/chat/ChatRealtimeHost';
import { ChatWidget } from '@/components/chat/ChatWidget';
import { useChatStore, unreadCounts } from '@/components/chat/chatStore';
import { ReferenceHighlight } from '@/components/ReferenceHighlight';

const Sidebar = ({ isOpen, toggle, isCollapsed, toggleCollapse, onRefresh, pendingReview }) => {
  const chatUnread = unreadCounts(useChatStore()).total; // misma burbuja que Verificar
  const location = useLocation();
  const { can } = usePermissions();

  const navItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/app' },
    { icon: TrendingUp, label: 'Finanzas', path: '/app/finances', module: 'finanzas' },
    { icon: FileText, label: 'Contratos', path: '/app/contracts', module: 'contratos' },
    { icon: CircleCheck, label: 'Ocupación', path: '/app/occupancy', module: 'ocupacion' },
    { icon: Building2, label: 'Propiedades', path: '/app/properties', module: 'propiedades' },
    { icon: Users, label: 'Inquilinos', path: '/app/tenants', module: 'inquilinos' },
    { icon: Briefcase, label: 'Empleados', path: '/app/employees', module: 'empleados' },
    { icon: Wrench, label: 'Mantenimiento', path: '/app/maintenance', module: 'mantenimiento' },
    { icon: AlertCircle, label: 'Servicios Ext.', path: '/app/external-services', module: 'servicios_externos' },
    { icon: ClipboardCheck, label: 'Verificar', path: '/app/review', module: 'verificar', badge: pendingReview },
    { icon: SquareKanban, label: 'Tareas', path: '/app/tasks', module: 'tareas' },
    { icon: MessageSquare, label: 'Chat', path: '/app/chat', module: 'chat', badge: chatUnread },
    { icon: Settings, label: 'Configuración', path: '/app/settings' },
  ].filter((item) => !item.module || can(item.module, 'ver') || (item.module === 'verificar' && (can('verificar', 'rechazados') || can('contratos', 'terminar') || can('contratos', 'renovar'))));

  const isActive = (path) => {
    if (path === '/app') return location.pathname === '/app';
    return location.pathname.startsWith(path);
  };

  return (
    <TooltipProvider delayDuration={0}>
      {/* Mobile Overlay */}
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={toggle} />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full bg-inmoblum-blue border-r border-inmoblum-blue-dark z-50
        transition-all duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        lg:translate-x-0
        ${isCollapsed ? 'lg:w-20' : 'lg:w-64'}
        w-64
      `}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className={`flex items-center h-16 px-4 border-b border-inmoblum-blue-light ${isCollapsed ? 'justify-center' : 'justify-between'}`}>
            {!isCollapsed && (
              <div className="flex items-center gap-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_rentmanage-hub/artifacts/mwgnpjwh_image.png" 
                  alt="Inmoblum Logo" 
                  className="w-9 h-9 object-contain"
                />
                <span className="text-xl font-semibold text-white" style={{ fontFamily: 'Poppins, sans-serif' }}>Inmoblum</span>
              </div>
            )}
            {isCollapsed && (
              <img 
                src="https://customer-assets.emergentagent.com/job_rentmanage-hub/artifacts/mwgnpjwh_image.png" 
                alt="Inmoblum Logo" 
                className="w-9 h-9 object-contain"
              />
            )}
            <Button variant="ghost" size="icon" className="lg:hidden text-white" onClick={toggle}>
              <X className="w-5 h-5" />
            </Button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 overflow-y-auto py-2 px-3">
            <ul className="space-y-0.5">
              {navItems.map((item, index) => {
                const Icon = item.icon;
                const active = item.path ? isActive(item.path) : false;
                
                // Action button (like Refresh)
                if (item.action) {
                  const actionContent = (
                    <button
                      onClick={() => {
                        if (item.action === 'refresh') {
                          onRefresh && onRefresh();
                        }
                        if (isOpen) toggle();
                      }}
                      className={`
                        w-full flex items-center gap-2.5 px-3 py-1.5 rounded-lg transition-all
                        text-white/80 hover:text-white hover:bg-inmoblum-blue-light
                        ${isCollapsed ? 'justify-center' : ''}
                      `}
                    >
                      <Icon className="w-[18px] h-[18px] flex-shrink-0" />
                      {!isCollapsed && (
                        <span className="text-[13.5px] font-medium flex-1 text-left">{item.label}</span>
                      )}
                    </button>
                  );

                  return (
                    <li key={`action-${index}`}>
                      {isCollapsed ? (
                        <Tooltip>
                          <TooltipTrigger asChild>{actionContent}</TooltipTrigger>
                          <TooltipContent side="right" className="bg-inmoblum-blue-light text-white border-inmoblum-blue">
                            {item.label}
                          </TooltipContent>
                        </Tooltip>
                      ) : actionContent}
                    </li>
                  );
                }
                
                // Regular navigation link
                const linkContent = (
                  <Link
                    to={item.path}
                    onClick={() => isOpen && toggle()}
                    className={`
                      flex items-center gap-2.5 px-3 py-1.5 rounded-lg transition-all relative
                      ${active 
                        ? 'bg-[#FF6B00] text-white shadow-lg shadow-[#FF6B00]/25' 
                        : 'text-white/80 hover:text-white hover:bg-inmoblum-blue-light'
                      }
                      ${isCollapsed ? 'justify-center' : ''}
                    `}
                  >
                    <div className="relative">
                      <Icon className={`w-[18px] h-[18px] flex-shrink-0 ${active ? 'text-white' : ''}`} />
                      {item.badge > 0 && isCollapsed && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] font-bold text-white flex items-center justify-center">
                          {item.badge > 9 ? '9+' : item.badge}
                        </span>
                      )}
                    </div>
                    {!isCollapsed && (
                      <span className="text-[13.5px] font-medium flex-1">{item.label}</span>
                    )}
                    {!isCollapsed && item.badge > 0 && (
                      <span className="bg-red-500 text-white text-[10px] font-bold px-1.5 py-px rounded-full leading-4">
                        {item.badge > 99 ? '99+' : item.badge}
                      </span>
                    )}
                  </Link>
                );

                return (
                  <li key={item.path}>
                    {isCollapsed ? (
                      <Tooltip>
                        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
                        <TooltipContent side="right" className="bg-inmoblum-blue-light text-white border-inmoblum-blue">
                          {item.label}
                        </TooltipContent>
                      </Tooltip>
                    ) : linkContent}
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Collapse Button (Desktop) */}
          <div className="hidden lg:block px-3 py-2 border-t border-inmoblum-blue-light">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleCollapse}
              className={`w-full text-white/80 hover:text-white hover:bg-inmoblum-blue-light ${isCollapsed ? 'justify-center' : 'justify-start'}`}
            >
              {isCollapsed ? <ChevronRight className="w-5 h-5" /> : <><ChevronLeft className="w-5 h-5 mr-2" /> Colapsar</>}
            </Button>
          </div>

        </div>
      </aside>
    </TooltipProvider>
  );
};

const DashboardLayout = () => {
  const { pathname } = useLocation();
  const { can, role } = usePermissions();
  // Badge: pendientes de verificar (si los ve) + solicitudes abiertas que puede atender (terminar/renovar contratos)
  const pendingReview = usePendingReviewCount(can('verificar', 'ver') || can('contratos', 'terminar') || can('contratos', 'renovar'), pathname);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const handleRefresh = () => {
    window.location.reload();
  };

  if (role === 'inquilino') return <Navigate to="/inquilino" replace />; // el inquilino solo ve su portal

  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar 
        isOpen={sidebarOpen} 
        toggle={() => setSidebarOpen(!sidebarOpen)}
        isCollapsed={sidebarCollapsed}
        toggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        onRefresh={handleRefresh}
        pendingReview={pendingReview}
      />
      
      {/* Main Content */}
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <TopBar onMenu={() => setSidebarOpen(true)} />

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <PresenceHeartbeat pathname={pathname} />
          <ChatRealtimeHost pathname={pathname} />
          <ChatWidget pathname={pathname} />
          <ReferenceHighlight />
          <RecordDetailsHost />
          <CommentDialogHost />
          <ProfileNameNotice pathname={pathname} />
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
